import {
  createContext,
  FormEvent,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { SessionIntegrity } from "./useSessionIntegrity";

const IntegrityContext = createContext<SessionIntegrity | undefined>(undefined);

export function ConsentStartPanel(props: {
  integrity: SessionIntegrity;
  title: string;
  detail?: ReactNode;
  onStart: () => void | Promise<void>;
  startLabel?: string;
}) {
  const { integrity } = props;
  const [agreed, setAgreed] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => setAgreed(false), [integrity.policyVersion]);

  if (!integrity.ready) {
    return (
      <div className="text-lg font-light text-gray-700">
        Menyiapkan sesi...
      </div>
    );
  }

  if (integrity.enabled && !integrity.accepted) {
    return (
      <div className="mx-auto w-full max-w-xl rounded-lg border border-gray-200 bg-white p-6 text-left shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">
          Pakta integritas
        </h1>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          Selama tes, tetaplah di halaman ini dan kerjakan jawaban secara
          mandiri. Perpindahan tab, tindakan keluar dari layar penuh, dan
          pintasan tertentu akan dicatat untuk assessor.
        </p>
        <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-md bg-gray-50 p-4">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(event) => setAgreed(event.currentTarget.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
          />
          <span className="text-sm leading-6 text-gray-700">
            Saya memahami ketentuan tes dan bersedia mengikutinya.
          </span>
        </label>
        {integrity.consentError && (
          <p className="mt-3 text-sm text-red-600">{integrity.consentError}</p>
        )}
        <button
          type="button"
          disabled={!agreed || integrity.accepting}
          onClick={() => void integrity.acceptConsent()}
          className="mt-5 inline-flex rounded-full border border-cyan-600 px-5 py-2 text-sm font-semibold text-cyan-700 hover:bg-cyan-600 hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
        >
          {integrity.accepting ? "Menyimpan..." : "Saya setuju"}
        </button>
      </div>
    );
  }

  const start = async () => {
    if (starting) return;
    setStarting(true);
    try {
      await integrity.requestFullscreen();
      await props.onStart();
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <div className="text-xl font-semibold text-gray-900">{props.title}</div>
      {props.detail && (
        <div className="mt-1 text-lg font-light text-gray-700">
          {props.detail}
        </div>
      )}
      <button
        type="button"
        disabled={starting}
        className="mt-4 items-center rounded-full border border-blue-500 bg-white px-4 py-2 text-base font-medium leading-6 text-blue-600 hover:bg-blue-600 hover:text-white focus:border-blue-400 disabled:cursor-wait disabled:border-gray-300 disabled:text-gray-400"
        onClick={() => void start()}
      >
        {starting ? "Memulai..." : props.startLabel ?? "Mulai"}
      </button>
    </>
  );
}

export function ProtectedTestContent(props: {
  integrity: SessionIntegrity;
  participantName: string;
  participantId: string | number;
  children: ReactNode;
}) {
  const { integrity } = props;
  const watermark = `${props.participantName} • ID ${props.participantId}`.toLocaleUpperCase(
    "id-ID"
  );

  const blockCopy = (event: FormEvent<HTMLDivElement>) => {
    if (!integrity.enabled) return;
    event.preventDefault();
    integrity.recordEvent("blocked_copy", { reason: "test_content" });
  };

  const blockContextMenu = (event: FormEvent<HTMLDivElement>) => {
    if (!integrity.enabled) return;
    event.preventDefault();
    integrity.recordEvent("blocked_contextmenu", {
      reason: "test_content",
    });
  };

  return (
    <IntegrityContext.Provider value={integrity}>
      <div
        data-integrity-test-content
        onCopy={blockCopy}
        onContextMenu={blockContextMenu}
        className="relative h-full overflow-hidden"
        style={integrity.enabled ? { userSelect: "none" } : undefined}
      >
        <div
          className="h-full transition duration-150"
          style={
            integrity.enabled && integrity.isAway
              ? { filter: "blur(12px)" }
              : undefined
          }
          aria-hidden={integrity.enabled && integrity.isAway}
        >
          {props.children}
        </div>
        {integrity.enabled && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-40 grid grid-cols-2 content-around overflow-hidden opacity-[0.12] sm:grid-cols-3"
          >
            {Array.from({ length: 18 }).map((_, index) => (
              <span
                key={index}
                className="-rotate-12 whitespace-nowrap px-4 text-center text-xs font-semibold text-gray-900"
              >
                {watermark}
              </span>
            ))}
          </div>
        )}
        {integrity.enabled && integrity.isAway && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/85 px-6 text-center text-white">
            <div>
              <div className="text-xl font-semibold">
                Kembali ke halaman tes
              </div>
              <p className="mt-2 text-sm text-gray-200">
                Waktu dan penyimpanan jawaban tetap berjalan saat halaman tidak
                aktif.
              </p>
            </div>
          </div>
        )}
      </div>
    </IntegrityContext.Provider>
  );
}

export function InTrayPasteGuard({ children }: { children: ReactNode }) {
  const integrity = useContext(IntegrityContext);

  return (
    <div
      data-integrity-intray-editor
      style={{ userSelect: "text" }}
      onPasteCapture={(event) => {
        if (!integrity?.enabled) return;
        event.preventDefault();
        integrity.recordEvent("blocked_paste", { reason: "intray_editor" });
      }}
    >
      {children}
    </div>
  );
}

export function FullscreenReturnBar(props: { integrity: SessionIntegrity }) {
  if (!props.integrity.fullscreenPrompt) return null;
  return (
    <div className="flex shrink-0 flex-col gap-3 bg-gray-900 px-4 py-3 text-sm text-white sm:flex-row sm:items-center sm:justify-between">
      <p>Anda keluar dari layar penuh. Kejadian ini tercatat. Tes tetap berjalan.</p>
      <button
        type="button"
        className="shrink-0 rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-100"
        onClick={() => void props.integrity.returnToFullscreen()}
      >
        Kembali ke layar penuh
      </button>
    </div>
  );
}

export function IntegrityWarning({
  message,
}: {
  message: string | undefined;
}) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-white/95 px-6 text-center">
      <div>
        <div className="text-xl font-semibold text-gray-900">
          Tindakan dibatasi
        </div>
        <p className="mt-2 text-gray-600">{message}</p>
      </div>
    </div>
  );
}
